import { useCallback, useRef, useState, useEffect, useMemo } from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { format, startOfWeek, endOfWeek, addWeeks, subWeeks, addDays, subDays, isSameDay } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useBookingsSupabaseStore } from '@/store/api/bookingsSupabaseStore';
import { useScheduleState } from '@/hooks/useScheduleState';
import { useRealtimeSchedule } from '@/hooks/useRealtimeSchedule';
import { Loader2, ChevronLeft, ChevronRight, Info, RefreshCw, CalendarSearch, LayoutGrid, List } from 'lucide-react';
import { WeeklySchedule } from '@/components/schedule/WeeklySchedule';
import { useIsMobile } from '@/hooks/use-mobile';
import { Badge } from '@/components/ui/badge';
import { Button } from "@/components/ui/button";
import { formatDate } from '@/utils/dateUtils';
import { cn } from '@/lib/utils';
import { useUserStore } from '@/store/userStore';
import { useSettingsStore } from '@/store/api/settingsStore';
import { useTranslation } from 'react-i18next';
import { Wifi, WifiOff } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { ScheduledClass } from '@/types/types';
import { getUserFriendlyError } from '@/utils/errorUtils';
import { useToast } from '@/hooks/use-toast';
import { scheduleClassesService } from '@/services/scheduleClassesService';
import { useAccessibleCalendars } from '@/hooks/useAccessibleCalendars';
import { CalendarSwitcher } from '@/components/schedule/CalendarSwitcher';
import { CalendarQuotaCards } from '@/components/schedule/CalendarQuotaCards';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
interface WeeklyScheduleViewProps {
  selectedDate: Date;
  onDateSelect: (date: Date) => void;
}

export function WeeklyScheduleView({ selectedDate, onDateSelect }: WeeklyScheduleViewProps) {
  const { user } = useUserStore();
  const { t } = useTranslation(['athlete', 'common']);
  const { settings } = useSettingsStore();
  const gymId = useUserStore((state) => state.gymId);
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [isRetrying, setIsRetrying] = useState(false);
  const [hasAutoNavigated, setHasAutoNavigated] = useState(false);
  // Bascule de planning pour un adhérent qui a accès à plusieurs calendriers
  // (ex. « Planning général » + « Cours Privés » via deux abonnements).
  // `null` = tous les plannings.
  const { accessibleCalendars } = useAccessibleCalendars(gymId ?? null);
  const [selectedCalendarId, setSelectedCalendarId] = useState<string | null>(null);
  // Maquette athlète : onglets « Jour » (liste des séances du jour) et
  // « Semaine » (grille heures × jours). La maquette VB20 ouvre sur la semaine.
  const [viewMode, setViewMode] = useState<'day' | 'week'>('week');
  const [isSearchingNextWeek, setIsSearchingNextWeek] = useState(false);
  
  const bookingAdvanceDays = settings?.schedule?.bookingAdvanceDays || 0;
  
  // ✅ Ref pour stocker la fonction de refresh silencieux
  const refreshClientDataRef = useRef<(() => Promise<void>) | null>(null);
  
  // ✅ Tracker les créneaux modifiés en temps réel
  const [realtimeModifiedSlots, setRealtimeModifiedSlots] = useState<Set<string>>(new Set());
  const previousClassesRef = useRef<ScheduledClass[]>([]);
  
  // ✅ UNIQUE source de données (comme l'admin)
  const {
    isLoading,
    classes,
    error,
    weekDates,
    handlePrevious: onPrevious,
    handleNext: onNext,
    handleDateSelect: onInternalDateSelect
  } = useScheduleState();

  // Keep the shared schedule hook aligned with the date controlled by the
  // athlete page. Previously an automatic jump only updated the page state,
  // leaving the hook on the old week and displaying the same empty result.
  useEffect(() => {
    onInternalDateSelect(selectedDate);
  }, [selectedDate, onInternalDateSelect]);
  
  // ✅ Realtime callbacks STABLES — invalident cache service + React Query.
  // La détection des slots modifiés est faite dans un useEffect séparé sur `classes`.
  const onScheduleChange = useCallback(() => {
    if (user?.id && gymId) {
      scheduleClassesService.invalidateCache(user.id, gymId);
    }
    queryClient.invalidateQueries({ queryKey: ['schedule-classes'] });
    queryClient.refetchQueries({ queryKey: ['schedule-classes'], type: 'active' });
  }, [queryClient, user?.id, gymId]);

  // ✅ Callback quand un booking change (realtime)
  const onBookingChange = useCallback(() => {
    if (user?.id && gymId) {
      scheduleClassesService.invalidateCache(user.id, gymId);
    }
    queryClient.invalidateQueries({ queryKey: ['schedule-classes'] });
    queryClient.refetchQueries({ queryKey: ['schedule-classes'], type: 'active' });

    if (refreshClientDataRef.current) {
      refreshClientDataRef.current();
    }
  }, [queryClient, user?.id, gymId]);

  // ✅ Détecter les changements de capacité/places via diff sur `classes`
  useEffect(() => {
    const previousClasses = previousClassesRef.current;
    if (previousClasses.length === 0) {
      previousClassesRef.current = classes;
      return;
    }

    const newModifiedSlots = new Set<string>();
    classes.forEach((newClass) => {
      const oldClass = previousClasses.find(
        (c) => c.id === newClass.id && c.class_date === newClass.class_date
      );
      if (!oldClass || oldClass.capacity !== newClass.capacity || oldClass.booked_count !== newClass.booked_count) {
        const slotKey = `${newClass.id}-${newClass.class_date}-${newClass.time}`;
        newModifiedSlots.add(slotKey);
        setTimeout(() => {
          setRealtimeModifiedSlots(prev => {
            const next = new Set(prev);
            next.delete(slotKey);
            return next;
          });
        }, 3000);
      }
    });

    if (newModifiedSlots.size > 0) {
      setRealtimeModifiedSlots(prev => new Set([...prev, ...newModifiedSlots]));
    }
    previousClassesRef.current = classes;
  }, [classes]);

  
  const { connectionStatus } = useRealtimeSchedule({
    enabled: !!gymId && !!user?.id,
    gymId: gymId || '',
    startDate: startOfWeek(selectedDate, { weekStartsOn: 1 }),
    endDate: endOfWeek(selectedDate, { weekStartsOn: 1 }),
    onScheduleChange,
    onBookingChange, // ✅ Utilisé maintenant !
    showNotifications: true
  });
  
  // ✅ isClassBooked via bookings store — chargé au montage pour que les
  // séances déjà réservées soient marquées même en arrivant directement ici.
  const { bookings, fetchBookings } = useBookingsSupabaseStore();
  useEffect(() => {
    if (user?.id && gymId) fetchBookings();
  }, [user?.id, gymId, fetchBookings]);
  
  const isClassBooked = useCallback((classId: string, date: Date, time: string) => {
    if (!bookings?.length || !date || !time) return false;
    const formattedDate = format(date, 'yyyy-MM-dd');
    const slotTime = time.substring(0, 5);
    return bookings.some(b => 
      b.classId === classId && 
      b.date === formattedDate && 
      b.userId === user?.id &&
      (b.time || '').substring(0, 5) === slotTime && 
      !['cancelled','canceled'].includes(b.status)
    );
  }, [bookings, user?.id]);

  // ✅ Bloquer navigation vers les semaines passées
  const today = new Date();
  const todayISO = format(today, 'yyyy-MM-dd');
  const currentWeekStart = startOfWeek(today, { weekStartsOn: 1 });
  const selectedWeekStart = startOfWeek(selectedDate, { weekStartsOn: 1 });
  const canGoBack = viewMode === 'day'
    ? selectedDate > today && !isSameDay(selectedDate, today)
    : selectedWeekStart > currentWeekStart;
  
  // ✅ Vérifier si on est sur la semaine courante (pour la navigation auto)
  const isCurrentWeek = useMemo(() => {
    return currentWeekStart.getTime() === selectedWeekStart.getTime();
  }, [currentWeekStart, selectedWeekStart]);
  
  // Navigation automatique vers le prochain JOUR avec un cours. Search the
  // already-loaded week first, then fetch one wider range in a single request.
  useEffect(() => {
    // Conditions pour tenter la navigation auto :
    // - Pas en chargement
    // - Pas d'erreur
    // - Aucun cours à venir cette semaine (semaine vide OU, comme un dimanche
    //   sans cours, uniquement des cours déjà passés)
    // - Première visite (pas encore navigué automatiquement)
    // - On est sur la semaine actuelle (pas une navigation manuelle)
    // - User et gymId disponibles
    const isOnToday = isSameDay(selectedDate, today);
    const nextLoadedClass = classes
      .filter((c) => c.class_date > todayISO)
      .sort((a, b) => a.class_date.localeCompare(b.class_date))[0];

    if (!isLoading && !error && isOnToday && !hasAutoNavigated && isCurrentWeek && user?.id && gymId) {
      const selectedDayHasClasses = classes.some((c) => c.class_date === todayISO);
      if (selectedDayHasClasses) return;

      const findNextDay = async () => {
        // Mark the attempt before awaiting to prevent concurrent searches.
        setHasAutoNavigated(true);
        setIsSearchingNextWeek(true);
        try {
          let nextDateISO = nextLoadedClass?.class_date;

          if (!nextDateISO) {
            const searchStart = addDays(today, 1);
            const searchEnd = addDays(today, 28);
            const upcomingClasses = await scheduleClassesService.fetchClassesWithUserContext(
              user.id,
              gymId,
              searchStart,
              searchEnd,
              false,
              selectedCalendarId
            );
            nextDateISO = upcomingClasses
              .filter((c) => c.class_date > todayISO)
              .sort((a, b) => a.class_date.localeCompare(b.class_date))[0]?.class_date;
          }

          if (nextDateISO) {
            const [year, month, day] = nextDateISO.split('-').map(Number);
            const nextDate = new Date(year, month - 1, day);
            onDateSelect(nextDate);
            onInternalDateSelect(nextDate);

            const nextDateFormatted = format(nextDate, 'dd MMMM', { locale: fr });
            
            toast({
              title: t("schedule.noClassesThisDay", { ns: "athlete", defaultValue: "Aucun cours aujourd’hui" }),
              description: t("schedule.showingWeekOf", {
                ns: "athlete",
                week: nextDateFormatted,
                defaultValue: `Prochains cours le ${nextDateFormatted}`,
              }),
            });
          }
        } catch (err) {
          console.error('[WeeklyScheduleView] Erreur lors de la recherche de cours:', err);
        } finally {
          setIsSearchingNextWeek(false);
        }
      };
      
      findNextDay();
    }
  }, [isLoading, error, classes, hasAutoNavigated, isCurrentWeek, selectedDate, user?.id, gymId, onDateSelect, onInternalDateSelect, toast, todayISO, selectedCalendarId]);

  // En vue Jour les flèches avancent/reculent d'un jour ouvré ; en vue
  // Semaine elles changent de semaine. Le passé reste interdit dans les deux.
  const openDays = settings?.schedule?.openDaysOfWeek;
  const handlePrevious = useCallback(() => {
    if (!canGoBack) return;
    if (viewMode === 'day') {
      let d = subDays(selectedDate, 1);
      while (openDays?.length && !openDays.includes(d.getDay()) && d > today) d = subDays(d, 1);
      onDateSelect(d);
      onInternalDateSelect(d);
      return;
    }
    onDateSelect(subWeeks(selectedDate, 1));
    onPrevious();
  }, [selectedDate, onDateSelect, onPrevious, onInternalDateSelect, canGoBack, viewMode, openDays]);

  const handleNext = useCallback(() => {
    if (viewMode === 'day') {
      let d = addDays(selectedDate, 1);
      while (openDays?.length && !openDays.includes(d.getDay())) d = addDays(d, 1);
      onDateSelect(d);
      onInternalDateSelect(d);
      return;
    }
    onDateSelect(addWeeks(selectedDate, 1));
    onNext();
  }, [selectedDate, onDateSelect, onNext, onInternalDateSelect, viewMode, openDays]);

  // ✅ Handler pour réessayer manuellement
  const handleRetry = useCallback(async () => {
    setIsRetrying(true);
    try {
      await queryClient.invalidateQueries({ queryKey: ['schedule-classes'] });
      await queryClient.refetchQueries({ queryKey: ['schedule-classes'] });
    } finally {
      setIsRetrying(false);
    }
  }, [queryClient]);

  // ✅ Auto-retry silencieux en cas d'erreur (toast informatif, pas bloquant)
  useEffect(() => {
    if (error && !isLoading && !isRetrying) {
      toast({
        title: t("connectionIssue", { ns: "common" }),
        description: getUserFriendlyError(error),
        variant: "destructive"
      });
      
      // Auto-retry après 5 secondes
      const timeout = setTimeout(() => {
        console.log('[WeeklyScheduleView] Auto-retrying after error...');
        handleRetry();
      }, 5000);
      
      return () => clearTimeout(timeout);
    }
  }, [error, isLoading, isRetrying, toast, handleRetry]);

  // ✅ Si erreur ET pas de données, afficher un message avec bouton retry (pas bloquant)
  if (error && !isLoading && classes.length === 0) {
    return (
      <div className="space-y-3">
        <Card>
          <CardContent className="py-8">
            <div className="text-center space-y-4">
              <div className="text-muted-foreground text-sm">
                {getUserFriendlyError(error)}
              </div>
              <Button 
                variant="outline" 
                onClick={handleRetry}
                disabled={isRetrying}
              >
                {isRetrying ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-2" />
                )}
                {t("retry", { ns: "common", defaultValue: "Réessayer" })}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ✅ État de recherche de la prochaine semaine avec cours
  if (isSearchingNextWeek) {
    return (
      <div className="flex flex-col justify-center items-center h-64 gap-3">
        <CalendarSearch className="h-8 w-8 text-muted-foreground animate-pulse" />
        <div className="text-center">
          <p className="text-sm font-medium text-foreground">
            {t("schedule.searchingNextWeek", { ns: "athlete", defaultValue: "Recherche des prochains cours..." })}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            {t("schedule.searchingNextWeekHint", { ns: "athlete", defaultValue: "Nous cherchons la prochaine semaine avec des cours disponibles" })}
          </p>
        </div>
      </div>
    );
  }

  if (isLoading && classes.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        <span className="ml-3 text-muted-foreground">
          {t('loading', { ns: "common" })}
        </span>
      </div>
    );
  }
  
  return (
    <div className="space-y-3">
      <Card className="athlete-surface mt-5">
        <CardContent className={isMobile ? "px-2 pb-2 pt-2" : "pt-6"}>
          {/* Compteurs de séances par planning (multi-abonnements seulement). */}
          <CalendarQuotaCards gymId={gymId ?? null} userId={user?.id} className="mb-4" />

          {/* La maquette place le choix de vue avant la date. */}
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
            {accessibleCalendars.length > 1 ? (
              <CalendarSwitcher
                calendars={accessibleCalendars}
                selectedCalendarId={selectedCalendarId}
                onSelect={setSelectedCalendarId}
                allLabel={t('schedule.allCalendars', { defaultValue: 'Tous' })}
                ariaLabel={t('schedule.calendarFilter', { defaultValue: 'Choisir un planning' })}
              />
            ) : (
              <span />
            )}

            <div
              className="athlete-segmented athlete-schedule-view-toggle inline-grid w-full grid-cols-2"
              role="group"
              aria-label={t('schedule.viewMode', { defaultValue: 'Affichage' })}
            >
              <Button
                type="button"
                variant="ghost"
                aria-pressed={viewMode === 'week'}
                onClick={() => setViewMode('week')}
                className={cn(
                   'h-10 justify-center gap-1.5 px-3 text-sm font-semibold',
                   viewMode === 'week'
                     ? 'is-active'
                    : 'text-muted-foreground hover:text-foreground'
                )}
              >
                <LayoutGrid className="h-4 w-4" />
                <span>{t('schedule.weekView', { defaultValue: 'Semaine' })}</span>
              </Button>
              <Button
                type="button"
                variant="ghost"
                aria-pressed={viewMode === 'day'}
                onClick={() => setViewMode('day')}
                className={cn(
                   'h-10 justify-center gap-1.5 px-3 text-sm font-semibold',
                   viewMode === 'day'
                     ? 'is-active'
                    : 'text-muted-foreground hover:text-foreground'
                )}
              >
                <List className="h-4 w-4" />
                <span>{t('schedule.dayView', { defaultValue: 'Jour' })}</span>
              </Button>
            </div>
          </div>

          <div className="athlete-date-nav mb-3 flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={handlePrevious} disabled={!canGoBack} aria-label={t('previous', { ns: 'common', defaultValue: 'Précédent' })}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div className="flex min-w-0 flex-1 items-center justify-center gap-2 rounded-md border border-border bg-card px-2 text-center font-semibold">
              <span className={isMobile ? "truncate text-xs" : "text-sm"}>
                {viewMode === 'day'
                  ? formatDate(selectedDate, 'PPPP')
                  : `${formatDate(weekDates[0] || selectedDate, 'PP')} - ${formatDate(weekDates[weekDates.length-1] || selectedDate, 'PP')}`}
              </span>
              <Badge
                variant="outline"
                className="h-5 shrink-0 px-1.5"
                aria-label={connectionStatus}
              >
                {connectionStatus === 'connected' ? <Wifi className="h-3 w-3" /> : connectionStatus === 'connecting' ? <Loader2 className="h-3 w-3 animate-spin" /> : <WifiOff className="h-3 w-3" />}
              </Badge>
            </div>
            <Button variant="outline" size="icon" onClick={handleNext} aria-label={t('next', { ns: 'common', defaultValue: 'Suivant' })}>
              <ChevronRight className="h-4 w-4" />
            </Button>
            {bookingAdvanceDays > 0 && (
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    aria-label={t('errors.bookingLimitInfo', { days: bookingAdvanceDays })}
                  >
                    <Info className="h-4 w-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-64 text-sm leading-relaxed">
                  {t('errors.bookingLimitInfo', { days: bookingAdvanceDays })}
                </PopoverContent>
              </Popover>
            )}
          </div>

          <WeeklySchedule
            weekDates={weekDates}
            selectedDate={selectedDate}
            onDateSelect={onDateSelect}
            onAddClass={() => {}}
            onEditClass={() => {}}
            classes={classes}
            isLoading={isLoading}
            isAdmin={false}
            selectedCalendarId={selectedCalendarId}
            viewMode={viewMode}
            refreshClasses={async () => {
              queryClient.invalidateQueries({ queryKey: ['schedule-classes'] });
            }}
            isClassBooked={isClassBooked}
            onMountRefreshCallback={(fn) => { refreshClientDataRef.current = fn; }}
            realtimeModifiedSlots={realtimeModifiedSlots}
          />
        </CardContent>
      </Card>
    </div>
  );
}
